import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Loader2, Calendar, ArrowLeft } from "lucide-react";
import api from "~/services/api";

const parseISO = (s) => new Date(s.replace(/-/g, "/"));
const format = (date, fmt) => {
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const MF = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  if (fmt === "MMM") return M[date.getMonth()];
  if (fmt === "MMMM yyyy") return `${MF[date.getMonth()]} ${date.getFullYear()}`;
  return date.toISOString();
};

const WhiteCard = ({ children }) => (
  <div className="relative">
    <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
      {children}
    </div>
    <div className="pointer-events-none absolute left-1/2 -bottom-[6px] -translate-x-1/2">
      <div className="relative">
        <div className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-gray-200" />
        <div className="absolute left-1/2 -top-[7px] -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-white" />
      </div>
    </div>
  </div>
);

const CustomMonthTooltip = ({ active, payload }) => {
  const [first, setFirst] = useState(true);
  useEffect(() => {
    if (active) {
      const t = setTimeout(() => setFirst(false), 50);
      return () => clearTimeout(t);
    }
    setFirst(true);
  }, [active]);

  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const monthName = format(parseISO(d.month + "-01"), "MMMM yyyy");

  return (
    <div className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
      <WhiteCard>
        <p className="mb-1 font-semibold">{monthName}</p>
        <p className="mb-2 text-sm">
          <span className="font-medium">{(d.students ?? 0).toLocaleString()}</span> students
        </p>
        <div className="border-t border-gray-200" />
        <p className="pt-2 text-xs text-gray-500">Click for more details</p>
      </WhiteCard>
    </div>
  );
};

const CustomMonthTooltipWithFilials = ({ active, payload, coordinate, hoveredIndex, totalBars }) => {
  const [first, setFirst] = useState(true);
  const [stableIndex, setStableIndex] = useState(null);

  useEffect(() => {
    if (active) {
      const t = setTimeout(() => setFirst(false), 50);
      return () => clearTimeout(t);
    }
    setFirst(true);
  }, [active]);

  useEffect(() => {
    if (hoveredIndex !== null) {
      setStableIndex(hoveredIndex);
    }
  }, [hoveredIndex]);

  if (!active || !payload?.length || !coordinate) return null;
  const d = payload[0].payload;
  const monthName = format(parseISO(d.month + "-01"), "MMMM yyyy");
  const filials = d.filials || [];

  const tooltipWidth = 220;
  const indexToUse = stableIndex !== null ? stableIndex : hoveredIndex;
  const isRightSide = indexToUse !== null && totalBars > 0 ? indexToUse >= totalBars * 0.65 : false;
  const leftPosition = isRightSide
    ? coordinate.x - tooltipWidth - 20
    : coordinate.x + 50;

  return (
    <div
      className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-x-2" : "opacity-100 translate-x-0"}`}
      style={{
        position: 'absolute',
        left: leftPosition,
        top: -80,
        transform: 'translateX(0)',
        pointerEvents: 'none'
      }}
    >
      <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
        <p className="mb-1 font-semibold">{monthName}</p>
        <p className="mb-2 text-sm">
          <span className="font-medium">{(d.students ?? 0).toLocaleString()}</span> students total
        </p>
        {filials.length > 0 && (
          <>
            <div className="border-t border-gray-200 my-2" />
            <div className="max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-600 mb-1">By Filial:</p>
              {filials.map((filial, idx) => (
                <div key={idx} className="text-xs text-gray-700 flex justify-between gap-3 py-0.5">
                  <span>{filial.name}</span>
                  <span className="font-medium">{filial.students}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="border-t border-gray-200 mt-2" />
        <p className="pt-2 text-xs text-gray-500">Click to see filials breakdown</p>
      </div>
    </div>
  );
};

const CustomCategoryTooltip = ({ active, payload }) => {
  const [first, setFirst] = useState(true);
  useEffect(() => {
    if (active) {
      const t = setTimeout(() => setFirst(false), 50);
      return () => clearTimeout(t);
    }
    setFirst(true);
  }, [active]);

  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const studentCount = d?.count ?? d?.students ?? 0;

  return (
    <div className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
      <div className="relative">
        <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
          <p className="mb-1 font-semibold">{d.name}</p>
          <p className="text-sm">
            <span className="font-medium">{studentCount.toLocaleString()}</span> students
          </p>
        </div>
        <div className="pointer-events-none absolute left-1/2 -bottom-[6px] -translate-x-1/2">
          <div className="relative">
            <div className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-gray-200" />
            <div className="absolute left-1/2 -top-[7px] -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

function WrapTick({ x, y, payload, maxCharsPerLine = 14 }) {
  const words = (payload?.value ?? "").split(" ");
  const lines = [];
  let current = "";
  for (const word of words) {
    if ((current + " " + word).trim().length > maxCharsPerLine && current.length > 0) {
      lines.push(current.trim());
      current = word;
    } else {
      current = (current + " " + word).trim();
    }
  }
  if (current) lines.push(current.trim());

  const lineHeight = 14;
  return (
    <g transform={`translate(${x},${y + 6})`}>
      {lines.map((line, i) => (
        <text key={i} x={0} y={i * lineHeight} textAnchor="middle" fontSize={12} fontWeight={600} fill="#1f2937">
          {line}
        </text>
      ))}
    </g>
  );
}

function MonthPillTickFactory({
  activeIndex,
  hoveredIndex,
  onTickClick,
  onTickHover,
  color = "#4C4C59",
  highlightFill = "#EEB39F",
  highlightText = "#111827",
}) {
  return function MonthPillTick(props) {
    const { x, y, payload, index } = props;
    const date = parseISO(payload.value + "-01");
    const label = format(date, "MMM");
    const hot = activeIndex === index || hoveredIndex === index;

    const pillW = 46;
    const pillH = 22;
    const rx = 12;

    return (
      <g
        transform={`translate(${x},${y + 14})`}
        cursor="pointer"
        onClick={() => onTickClick?.(index)}
        onMouseEnter={() => onTickHover?.(index)}
        onMouseLeave={() => onTickHover?.(null)}
      >
        <rect
          x={-pillW / 2}
          y={-pillH + 2}
          width={pillW}
          height={pillH}
          rx={rx}
          fill={hot ? highlightFill : "#ffffff"}
          stroke={hot ? highlightFill : color}
          opacity={hot ? 1 : 0.35}
        />
        <text
          textAnchor="middle"
          dy={-6}
          className="text-[12px] font-semibold"
          fill={hot ? highlightText : color}
        >
          {label}
        </text>
      </g>
    );
  };
}

export default function EnrollmentBarChart({ year }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [summary, setSummary] = useState({ inClass: 0, waiting: 0, total: 0, waitingBySubstatus: [], waitingForecastTotals: null });
  const [showWaitingDetail, setShowWaitingDetail] = useState(false);
  const waitingRef = useRef(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [viewMode, setViewMode] = useState("months");
  const [processData, setProcessData] = useState(null);
  const [processLoading, setProcessLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [isAllFilial, setIsAllFilial] = useState(false);
  const [filialsData, setFilialsData] = useState(null);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [selectedFilial, setSelectedFilial] = useState(null);

  const didAnimateMonths = useRef(false);
  const didAnimateCats = useRef(false);
  const didAnimateFilials = useRef(false);

  const activeYear = typeof year === "number" ? year : new Date().getFullYear();
  const filial = useSelector((state) => state.auth?.filial);

  async function fetchData() {
    try {
      setLoading(true);
      const params = {};
      if (typeof year === "number") params.year = year;
      const resp = await api.get("/enrollment-stats/month", { params });
      const raw = resp.data || [];

      if (raw.all_filial === true && raw.months) {
        setIsAllFilial(true);
        setFilialsData(raw.months);
        setData(raw.months.map(d => ({
          month: d.month,
          students: Number(d.students ?? 0),
          filials: d.filials || []
        })));
      } else if (raw.all_filial === false && raw.months) {
        setIsAllFilial(false);
        setFilialsData(null);
        setData(raw.months.map(d => ({ month: d.month, students: Number(d.students ?? 0) })));
      } else {
        setIsAllFilial(false);
        setFilialsData(null);
        setData(raw.map(d => ({ month: d.month, students: Number(d.students ?? 0) })));
      }
    } catch {
      setData([]);
      setIsAllFilial(false);
      setFilialsData(null);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSummary() {
    try {
      setSummaryLoading(true);
      const resp = await api.get("/enrollment-stats/summary");
      const r = resp.data || {};
      setSummary({
        inClass: r.in_class || 0,
        waiting: r.waiting || 0,
        total: r.total || 0,
        waitingBySubstatus: r.waiting_by_process_substatus || [],
        waitingForecastTotals: r.waiting_forecast_totals || null,
      });
    } catch {
      setSummary({ inClass: 0, waiting: 0, total: 0, waitingBySubstatus: [], waitingForecastTotals: null });
    } finally {
      setSummaryLoading(false);
    }
  }

  async function fetchProcessData(month, filialId = null) {
    try {
      setProcessLoading(true);
      const params = { month };

      if (filialId !== null && filial?.id === 1) {
        params.filial_id = filialId;
      }

      const resp = await api.get("/enrollment-stats/process-by-month", { params });
      setProcessData(resp.data);
      setSelectedMonth(month);
      setViewMode("categories");
      didAnimateCats.current = false;
    } catch {
    } finally {
      setProcessLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    fetchSummary();
    didAnimateMonths.current = false;
  }, [year]);

  useEffect(() => {
    if (!showWaitingDetail) return;
    const handleClickOutside = (event) => {
      if (waitingRef.current && !waitingRef.current.contains(event.target)) {
        setShowWaitingDetail(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showWaitingDetail]);

  
  useEffect(() => {
    
    fetchData();
    fetchSummary();
    didAnimateMonths.current = false;

   
    if (selectedMonth) {
      fetchProcessData(selectedMonth);
    }
  }, [filial?.id]);

  const handleMonthClick = (entry, index) => {
    setActiveIndex(index);

    if (isAllFilial && entry?.filials) {
      setSelectedMonthData(entry);
      setSelectedMonth(entry.month);
      setViewMode("filials");
    } else if (entry?.month) {
      fetchProcessData(entry.month);
    }
  };

  const handleFilialClick = (filialEntry) => {
    if (selectedMonth && filialEntry?.id) {
      setSelectedFilial(filialEntry);
      fetchProcessData(selectedMonth, filialEntry.id);
    }
  };

  const handleBackClick = () => {
    if (viewMode === "categories") {
      if (isAllFilial && selectedFilial) {
        setViewMode("filials");
        setProcessData(null);
        setSelectedFilial(null);
      } else {
        setViewMode("months");
        setProcessData(null);
        setSelectedMonth(null);
        setActiveIndex(null);
        setSelectedFilial(null);
      }
    } else if (viewMode === "filials") {
      setViewMode("months");
      setSelectedMonthData(null);
      setSelectedMonth(null);
      setActiveIndex(null);
      setSelectedFilial(null);
    } else {
      setViewMode("months");
      setProcessData(null);
      setSelectedMonth(null);
      setActiveIndex(null);
      setSelectedFilial(null);
    }
  };

  if (loading || processLoading) {
    return (
      <div className="w-full p-3 px-5 border rounded-lg">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="animate-spin" size={36} />
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="w-full p-3 px-5 border rounded-lg">
        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
          <Calendar className="w-14 h-14 mb-2" />
          <p className="text-base">No enrollment data for {activeYear}</p>
        </div>
      </div>
    );
  }

  const MonthPillTick = MonthPillTickFactory({
    activeIndex,
    hoveredIndex,
    onTickClick: (idx) => {
      const entry = data[idx];
      if (!entry) return;
      setActiveIndex(idx);
      if (entry?.month) fetchProcessData(entry.month);
    },
    onTickHover: (idx) => setHoveredIndex(idx),
    color: "#4C4C59",
    highlightFill: "#EEB39F",
    highlightText: "#111827",
  });

  return (
    <div className="w-full p-3 px-5 border rounded-lg">
      <div className="flex flex-row items-center justify-between mb-5">
        <div className="flex flex-col items-start justify-center">
          <div className="flex items-center gap-2">
            {(viewMode === "categories" || viewMode === "filials") && (
              <button
                onClick={handleBackClick}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-bold">
              {viewMode === "months" && "Students in Class"}
              {viewMode === "filials" && "Students in Class - By Filial"}
              {viewMode === "categories" && "Students In Class - Process Type"}
            </h1>
          </div>

          {viewMode === "months" ? (
            <div className="text-base">
              <span className="text-gray-500">This year</span>
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-semibold text-gray-900">{activeYear}</span>
            </div>
          ) : viewMode === "filials" ? (
            <h1 className="text-base font-extralight text-gray-500">
              {`${format(parseISO(selectedMonth + "-01"), "MMMM yyyy")} - `}
              <span className="font-bold">Total: {selectedMonthData?.students || 0} students</span>
            </h1>
          ) : (
            <h1 className="text-base font-extralight text-gray-500">
              {`${format(parseISO(selectedMonth + "-01"), "MMMM yyyy")}`}
              {selectedFilial && (
                <>
                  <span className="mx-1">-</span>
                  <span className="font-semibold text-gray-700">{selectedFilial.name}</span>
                </>
              )}
              <span className="mx-1">-</span>
              <span className="font-bold">Total: {processData?.total_active_students || 0} students</span>
            </h1>
          )}
        </div>

        {viewMode === "months" ? (
          <div className="flex flex-row items-end justify-center gap-4">
            <div className="text-xl font-bold border-b border-l p-2 rounded-sm w-40">
              <h2 className="text-sm font-light text-gray-500">In Class</h2>
              {summaryLoading ? (
                <div className="h-7 w-24 bg-gray-200 animate-pulse rounded-sm mt-1" />
              ) : (
                <span>{summary.inClass.toLocaleString()}</span>
              )}
            </div>
            <div ref={waitingRef} className="relative">
              <button
                type="button"
                onClick={() => setShowWaitingDetail((prev) => !prev)}
                disabled={summaryLoading || !summary.waitingBySubstatus?.length}
                className="text-xl font-bold border-b border-l p-2 rounded-sm w-40 text-left cursor-pointer disabled:cursor-default hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-base font-light text-gray-500">Waiting</h2>
                {summaryLoading ? (
                  <div className="h-7 w-24 bg-gray-200 animate-pulse rounded-sm mt-1" />
                ) : (
                  <span>{summary.waiting.toLocaleString()}</span>
                )}
              </button>

              {showWaitingDetail && !!summary.waitingBySubstatus?.length && (
                <div className="absolute top-full right-0 mt-2 z-20 w-max">
                  <WhiteCard>
                    <p className="mb-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                      Waiting by substatus
                    </p>
                    <table className="text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="text-left font-normal text-xs text-gray-400 pb-1 pr-6"></th>
                          <th className="text-right font-normal text-xs text-gray-400 pb-1 px-3 whitespace-nowrap">0-30</th>
                          <th className="text-right font-normal text-xs text-gray-400 pb-1 px-3 whitespace-nowrap">31-60</th>
                          <th className="text-right font-normal text-xs text-gray-400 pb-1 px-3 whitespace-nowrap">&gt;61</th>
                          <th className="text-right font-normal text-xs text-gray-400 pb-1 px-3 whitespace-nowrap">Not Defined</th>
                          <th className="text-right font-normal text-xs text-gray-400 pb-1 pl-3 whitespace-nowrap">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.waitingBySubstatus.map((item) => {
                          const rowTotal =
                            item.total ??
                            (item.forecast?.["0-30"] ?? 0) +
                              (item.forecast?.["31-60"] ?? 0) +
                              (item.forecast?.["61+"] ?? 0) +
                              (item.forecast?.["Not Defined"] ?? 0);
                          return (
                            <tr key={item.id ?? item.name}>
                              <td className="text-gray-600 whitespace-nowrap pr-6 py-0.5">{item.name}</td>
                              <td className="text-right text-gray-700 px-3 py-0.5 tabular-nums whitespace-nowrap">{(item.forecast?.["0-30"] ?? 0).toLocaleString()}</td>
                              <td className="text-right text-gray-700 px-3 py-0.5 tabular-nums whitespace-nowrap">{(item.forecast?.["31-60"] ?? 0).toLocaleString()}</td>
                              <td className="text-right text-gray-700 px-3 py-0.5 tabular-nums whitespace-nowrap">{(item.forecast?.["61+"] ?? 0).toLocaleString()}</td>
                              <td className="text-right text-gray-700 px-3 py-0.5 tabular-nums whitespace-nowrap">{(item.forecast?.["Not Defined"] ?? 0).toLocaleString()}</td>
                              <td className="text-right font-semibold text-gray-900 pl-3 py-0.5 tabular-nums whitespace-nowrap">{rowTotal.toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {summary.waitingForecastTotals && (
                        <tfoot>
                          <tr className="border-t border-gray-200">
                            <td className="text-gray-900 font-semibold whitespace-nowrap pr-6 pt-1">Total</td>
                            <td className="text-right text-gray-900 font-semibold px-3 pt-1 tabular-nums whitespace-nowrap">{(summary.waitingForecastTotals["0-30"] ?? 0).toLocaleString()}</td>
                            <td className="text-right text-gray-900 font-semibold px-3 pt-1 tabular-nums whitespace-nowrap">{(summary.waitingForecastTotals["31-60"] ?? 0).toLocaleString()}</td>
                            <td className="text-right text-gray-900 font-semibold px-3 pt-1 tabular-nums whitespace-nowrap">{(summary.waitingForecastTotals["61+"] ?? 0).toLocaleString()}</td>
                            <td className="text-right text-gray-900 font-semibold px-3 pt-1 tabular-nums whitespace-nowrap">{(summary.waitingForecastTotals["Not Defined"] ?? 0).toLocaleString()}</td>
                            <td className="text-right text-gray-900 font-semibold pl-3 pt-1 tabular-nums whitespace-nowrap">
                              {(
                                (summary.waitingForecastTotals["0-30"] ?? 0) +
                                (summary.waitingForecastTotals["31-60"] ?? 0) +
                                (summary.waitingForecastTotals["61+"] ?? 0) +
                                (summary.waitingForecastTotals["Not Defined"] ?? 0)
                              ).toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </WhiteCard>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-row items-end justify-center gap-4">
            <div className="text-xl font-bold border-b border-l p-2 rounded-sm w-40">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-xs bg-[#403D8A]" />
                <h2 className="text-sm font-light text-gray-500">F1</h2>
              </div>
              <span>{processData?.process_types?.f1?.toLocaleString() || 0}</span>
            </div>
            <div className="text-xl font-bold border-b border-l p-2 rounded-sm w-40">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-xs bg-[#EE5827]" />
                <h2 className="text-base font-light text-gray-500">Non-F1</h2>
              </div>
              <span>{processData?.process_types?.non_f1?.toLocaleString() || 0}</span>
            </div>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={150}>
        {viewMode === "months" ? (
          <BarChart
            data={data}
            barCategoryGap="80%"
            margin={{ top: 0, right: 0, bottom: 16, left: 0 }}
          >
            <Tooltip
              content={isAllFilial ? <CustomMonthTooltipWithFilials hoveredIndex={hoveredIndex} totalBars={data.length} /> : <CustomMonthTooltip />}
              position={isAllFilial ? undefined : { y: -80 }}
              offset={isAllFilial ? 0 : -65}
              cursor={{ fill: "transparent" }}
              wrapperStyle={isAllFilial ? { pointerEvents: 'none', position: 'static' } : {}}
            />
            <CartesianGrid vertical={false} strokeOpacity={0.15} />
            <XAxis
              dataKey="month"
              tick={<MonthPillTick />}
              axisLine={false}
              tickLine={false}
              height={28}
              tickMargin={8}
            />
            <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} />
            <defs>
              <style>{`.bar-label { transition: fill 0.2s ease; }`}</style>
            </defs>
            <Bar
              dataKey="students"
              onClick={(e, idx) => handleMonthClick(data[idx], idx)}
              radius={[8, 8, 8, 8]}
              isAnimationActive={!didAnimateMonths.current}
              animationBegin={80}
              animationDuration={600}
              animationEasing="ease-out"
              onAnimationEnd={() => (didAnimateMonths.current = true)}
              onMouseMove={(_, idx) => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <LabelList
                dataKey="students"
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, value, index } = props;
                  const val = Number(value ?? 0);
                  const hot = hoveredIndex === index || activeIndex === index;
                  const isZeroOrLow = val === 0 || height < 25;
                  const fill = isZeroOrLow ? "#111827" : (hot ? "#111827" : "#FFFFFF");
                  const yPos = isZeroOrLow ? y - 8 : y + height - 12;
                  
                  return (
                    <text
                      x={x + width / 2}
                      y={yPos}
                      fill={fill}
                      textAnchor="middle"
                      className="text-[14px] font-semibold bar-label"
                      pointerEvents="none"
                    >
                      {val.toLocaleString()}
                    </text>
                  );
                }}
              />
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  cursor="pointer"
                  fill={
                    hoveredIndex === index || (activeIndex !== null && index === activeIndex)
                      ? "#EEB39F"
                      : "#EE5827"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        ) : viewMode === "filials" ? (
          <BarChart
            data={selectedMonthData?.filials || []}
            margin={{ top: 20, right: 0, bottom: 16, left: 0 }}
          >
            <Tooltip content={<CustomCategoryTooltip />} position={{ y: -60 }} offset={-50} cursor={{ fill: "transparent" }} />
            <CartesianGrid vertical={false} strokeOpacity={0.15} />
            <XAxis dataKey="name" tick={<WrapTick />} interval={0} axisLine={false} tickLine={false} height={60} />
            <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} domain={[0, (max) => Math.round(max * 1.1)]} axisLine={false} tickLine={false} />
            <Bar
              dataKey="students"
              radius={[8, 8, 8, 8]}
              isAnimationActive={!didAnimateFilials.current}
              animationBegin={80}
              animationDuration={600}
              animationEasing="ease-out"
              onAnimationEnd={() => (didAnimateFilials.current = true)}
              onClick={(e, idx) => handleFilialClick(selectedMonthData?.filials[idx])}
            >
              <LabelList
                dataKey="students"
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, value } = props;
                  const val = Number(value ?? 0);
                  const isLowBar = height < 30;
                  const yPos = isLowBar ? y - 8 : y + height - 12;

                  return (
                    <text
                      x={x + width / 2}
                      y={yPos}
                      textAnchor="middle"
                      className="text-[14px] font-semibold"
                      fill={isLowBar ? "#111827" : "#FFFFFF"}
                      pointerEvents="none"
                    >
                      {val.toLocaleString()}
                    </text>
                  );
                }}
              />
              {(selectedMonthData?.filials || []).map((_, i) => (
                <Cell
                  key={`cell-${i}`}
                  cursor="pointer"
                  fill="#EE5827"
                />
              ))}
            </Bar>
          </BarChart>
        ) : (
          <BarChart
            data={processData?.categories || []}
            margin={{ top: 20, right: 0, bottom: 16, left: 0 }}
          >
            <Tooltip content={<CustomCategoryTooltip />} position={{ y: -60 }} offset={-50} cursor={{ fill: "transparent" }} />
            <CartesianGrid vertical={false} strokeOpacity={0.15} />
            <XAxis dataKey="name" tick={<WrapTick />} interval={0} axisLine={false} tickLine={false} height={60} />
            <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} domain={[0, (max) => Math.round(max * 1.1)]} axisLine={false} tickLine={false} />
            <Bar
              dataKey="count"
              radius={[8, 8, 8, 8]}
              isAnimationActive={!didAnimateCats.current}
              animationBegin={80}
              animationDuration={600}
              animationEasing="ease-out"
              onAnimationEnd={() => (didAnimateCats.current = true)}
            >
              <LabelList
                dataKey="count"
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, value } = props;
                  const val = Number(value ?? 0);
                  const isLowBar = height < 30;
                  const yPos = isLowBar ? y - 8 : y + height - 12;
                  
                  return (
                    <text
                      x={x + width / 2}
                      y={yPos}
                      textAnchor="middle"
                      className="text-[14px] font-semibold"
                      fill={isLowBar ? "#111827" : "#FFFFFF"}
                      pointerEvents="none"
                    >
                      {val.toLocaleString()}
                    </text>
                  );
                }}
              />
              {(processData?.categories || []).map((category, i) => (
                <Cell
                  key={`cell-${i}`}
                  fill={
                    category?.process_type === "F1"
                      ? "#403D8A"
                      : category?.process_type === "Non-F1"
                        ? "#EE5827"
                        : i < 4
                          ? "#403D8A"
                          : "#EE5827"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
